import { api } from "./client";

/**
 * Certifications Module v1.0 — admin frontend surface for the cross-entity
 * CertificationDefinition catalog. Mirrors the C# DTOs in
 * <c>src/PhoenixCapital.Core/DTOs/Certifications/CertificationDtos.cs</c>
 * (PR 1) and the admin endpoints on
 * <c>CertificationDefinitionsController</c>.
 *
 * The whole surface lives behind <c>FeatureFlags:Certifications</c>; calls
 * return 404 when the flag is off and the page renders a "feature not
 * available" notice without erroring.
 */

export type EntityType =
  | "Broker"
  | "Loan"
  | "Deal"
  | "Vendor"
  | "Spv"
  | "Investor"
  | "SpvPool"
  | "Brokerage";

export const ENTITY_TYPES: EntityType[] = [
  "Broker",
  "Loan",
  "Deal",
  "Vendor",
  "Spv",
  "Investor",
  "SpvPool",
  "Brokerage",
];

export interface CertificationDefinitionDto {
  id: string;
  entityType: EntityType;
  stageFrom: string | null;
  stageTo: string;
  label: string;
  description: string | null;
  requiredRoles: string[];
  revokeRoles: string[];
  sortOrder: number;
  active: boolean;
  createdAt: string;
  createdById: string | null;
  modifiedAt: string | null;
  modifiedById: string | null;
}

export interface CreateCertificationDefinitionDto {
  entityType: EntityType;
  stageFrom: string | null;
  stageTo: string;
  label: string;
  description: string | null;
  requiredRoles: string[];
  revokeRoles: string[];
  sortOrder: number;
}

export interface UpdateCertificationDefinitionDto {
  label: string;
  description: string | null;
  requiredRoles: string[];
  revokeRoles: string[];
  sortOrder: number;
}

export interface StageDescriptorDto {
  code: string;
  displayName: string;
  order: number;
}

export interface CertificationDefinitionListQuery {
  entityType?: EntityType;
  active?: boolean;
  stageTo?: string;
}

export const certificationDefinitionsApi = {
  list: (query: CertificationDefinitionListQuery = {}) =>
    api.get<CertificationDefinitionDto[]>("/admin/certification-definitions", query),
  get: (id: string) =>
    api.get<CertificationDefinitionDto>(`/admin/certification-definitions/${id}`),
  create: (body: CreateCertificationDefinitionDto) =>
    api.post<CertificationDefinitionDto>("/admin/certification-definitions", body),
  update: (id: string, body: UpdateCertificationDefinitionDto) =>
    api.put<CertificationDefinitionDto>(`/admin/certification-definitions/${id}`, body),
  deactivate: (id: string) =>
    api.patch<void>(`/admin/certification-definitions/${id}/deactivate`),
  reactivate: (id: string) =>
    api.patch<void>(`/admin/certification-definitions/${id}/reactivate`),
  stages: (entityType: EntityType) =>
    api.get<StageDescriptorDto[]>("/admin/certification-definitions/stages", { entityType }),
};

/* =====================================================================
 * Operational surface — per-entity records + gate evaluation
 * (Module §3b, §4b, §4c). Mirrors CertificationsController.
 * ==================================================================== */

export interface CertificationRecordDto {
  id: string;
  definitionId: string;
  definitionLabel: string;
  entityType: EntityType;
  entityId: string;
  certifiedByUserId: string;
  certifiedByDisplayName: string | null;
  certifiedAt: string;
  note: string | null;
  revokedAt: string | null;
  revokedByUserId: string | null;
  revokedByDisplayName: string | null;
  revokedReason: string | null;
  certifiedAsRoles: string[];
  revokedAsRoles: string[];
}

export interface GateItemDto {
  definitionId: string;
  label: string;
  description: string | null;
  sortOrder: number;
  requiredRoles: string[];
  revokeRoles: string[];
  activeRecord: CertificationRecordDto | null;
}

export interface GateDto {
  stageFrom: string | null;
  stageTo: string;
  items: GateItemDto[];
}

export interface EntityCertificationsDto {
  currentGate: GateDto | null;
  upcomingGates: GateDto[];
  history: CertificationRecordDto[];
}

export interface MissingItemDto {
  definitionId: string;
  label: string;
}

export interface GateEvaluationResultDto {
  eligible: boolean;
  missing: MissingItemDto[];
}

export interface CreateCertificationRecordDto {
  definitionId: string;
  note: string | null;
}

export interface RevokeCertificationRecordDto {
  reason: string;
}

/**
 * Lower-cased EntityType for the {entityType} route segment, per
 * <c>CertificationsController.TryParseEntityType</c>.
 */
function entitySegment(entityType: EntityType): string {
  return entityType.toLowerCase();
}

export const certificationsApi = {
  listForEntity: (entityType: EntityType, entityId: string) =>
    api.get<EntityCertificationsDto>(
      `/${entitySegment(entityType)}/${entityId}/certifications`,
    ),
  certify: (entityType: EntityType, entityId: string, body: CreateCertificationRecordDto) =>
    api.post<CertificationRecordDto>(
      `/${entitySegment(entityType)}/${entityId}/certifications`,
      body,
    ),
  revoke: (
    entityType: EntityType,
    entityId: string,
    recordId: string,
    body: RevokeCertificationRecordDto,
  ) =>
    api.post<CertificationRecordDto>(
      `/${entitySegment(entityType)}/${entityId}/certifications/${recordId}/revoke`,
      body,
    ),
};
